import { Action as BaseAction } from 'redux'
import { ThunkAction } from 'redux-thunk'

import { notify } from './notifications'
import { fetchActivities } from '../actions/context/setActivities'
import { Filters, Type } from '../types/filters'
import { Action as ActionType, Dispatch } from '../types/reducer'
import { CadenceState, GetState, RootState } from '../types/state'
import { Cadence, Query } from '../types/cadence'
import { ExtraArgument } from '../types/thunk'
import { messages } from './messages'
import { intl } from '../platform/localization'

export const SET_CADENCES = 'cadences/SET_CADENCES'
export const SET_TEAM_CADENCES = 'cadences/SET_TEAM_CADENCES'
export const SET_CADENCE_ACTIVE_TAB = 'cadences/SET_CADENCE_ACTIVE_TAB'

const initialState: CadenceState = {
  activeTab: Type.PERSONAL,
  items: {
    personal: [],
    team: [],
  },
}

type SetCadencesAction = ActionType<typeof SET_CADENCES, Cadence[]>
type SetTeamCadencesAction = ActionType<typeof SET_TEAM_CADENCES, Cadence[]>
type SetCadenceActiveTabAction = ActionType<typeof SET_CADENCE_ACTIVE_TAB, string>
type UnknownThunkAction = ThunkAction<unknown, RootState, ExtraArgument, BaseAction>

type Action = SetCadencesAction | SetTeamCadencesAction | SetCadenceActiveTabAction

export default function reducer(state: CadenceState = initialState, { type, payload }: Action) {
  switch (type) {
    case SET_CADENCES:
      return {
        ...state,
        items: {
          personal: payload,
          team: state.items.team,
        },
      }
    case SET_TEAM_CADENCES:
      return {
        ...state,
        items: {
          personal: state.items.personal,
          team: payload,
        },
      }
    case SET_CADENCE_ACTIVE_TAB:
      return {
        ...state,
        activeTab: payload,
      }
    default:
      return state
  }
}

export function setCadences(cadences: Cadence[]): SetCadencesAction {
  return {
    type: SET_CADENCES,
    payload: cadences,
  }
}

export function setTeamCadences(cadences: Cadence[]): SetTeamCadencesAction {
  return {
    type: SET_TEAM_CADENCES,
    payload: cadences,
  }
}

export function setCadenceActiveTab(tab: Type): SetCadenceActiveTabAction {
  return {
    type: SET_CADENCE_ACTIVE_TAB,
    payload: tab,
  }
}

export const getCadencesByType = (type: Type, filters: Filters) => (
  dispatch: Dispatch<SetCadencesAction | SetTeamCadencesAction, void>,
  getState: GetState,
  { gateway }: ExtraArgument
) => {
  const isPersonal = type === Type.PERSONAL
  const path = isPersonal
    ? gateway.cadences.PERSONAL_CADENCES_PATH
    : gateway.cadences.TEAM_CADENCES_PATH
  const actionCreator = isPersonal ? setCadences : setTeamCadences

  const query: Query = {
    query: filters.query,
    sort: 'name',
  }

  if (!isPersonal) {
    query.filter_group_ids = filters.groups.map(({ id }) => id)
    query.all = true
  }

  return gateway.cadences
    .fetchAll(path, 1, query)
    .then((cadences: Cadence[]) => dispatch(actionCreator(cadences || [])))
}

export const getCadences = (queryParams: Query = {}) => (
  dispatch: Dispatch<SetCadencesAction, void>,
  _: GetState,
  { gateway }: ExtraArgument
) =>
  gateway.cadences
    .all(gateway.cadences.PERSONAL_CADENCES_PATH, 1, queryParams)
    .then((data: Cadence[]) => dispatch(setCadences(data || [])))

export const getTeamCadences = (queryParams: Query = {}) => (
  dispatch: Dispatch<SetTeamCadencesAction, void>,
  _: GetState,
  { gateway }: ExtraArgument
) =>
  gateway.cadences
    .all(gateway.cadences.TEAM_CADENCES_PATH, 1, queryParams)
    .then((cadences: Cadence[]) => dispatch(setTeamCadences(cadences || [])))

export const addPersonToCadence = (
  cadenceId: number,
  personId: number,
  personName: string,
  { stepId = null, assignToId = null }: { stepId?: number | null; assignToId?: number | null } = {}
) => async (
  dispatch: Dispatch<UnknownThunkAction, void>,
  _: GetState,
  { gateway }: ExtraArgument
) => {
  const { cadence, meta } = await gateway.cadences.add({ cadenceId, personId, stepId, assignToId })

  if (meta.added_count) {
    dispatch(fetchActivities(personId))
    dispatch(notify(messages.textNotifyPersonAddedToCadence(personName, cadence.name)))
  } else {
    dispatch(
      notify({
        type: 'error',
        message: messages.textNotifyUnableToAssignCadenceToUser,
      })
    )
  }
}

export const removePersonFromCadence = (
  cadenceId: number,
  personId: number,
  personName: string
) => async (
  dispatch: Dispatch<UnknownThunkAction, void>,
  _: GetState,
  { gateway }: ExtraArgument
) => {
  const cadence = await gateway.cadences.remove(cadenceId, personId)
  dispatch(fetchActivities(personId))
  dispatch(notify(messages.textNotifyPersonRemovedFromCadence(personName, cadence.name)))
}

export const changeDueDateForCadence = (activityId: number, date: Date, personId: number) => async (
  dispatch: Dispatch<UnknownThunkAction, void>,
  getState: GetState,
  { gateway }: ExtraArgument
) => {
  await gateway.actions.changeDue(activityId, date)
  dispatch(fetchActivities(personId))

  const isModernized = !!getState()?.currentUser?.features?.outlook_person_modernization

  if (isModernized) {
    const formattedDate = intl.formatDate(date)
    dispatch(notify(messages.textNotifyChangeDueDateForCadenceWithDate(formattedDate)))
  } else {
    dispatch(notify(messages.textNotifyChangeDueDateForCadence))
  }
}
